import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FullQuizData } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { QuestionPreviewComponent } from '../question-preview/question-preview.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PDFFailedQuestionDetail } from '@edtech-ui/core/models/quiz-creation/get-quiz-pdf-docs-upload';

@Component({
	selector: 'app-pdt-doc-mode',
	standalone: true,
	imports: [CommonModule, QuestionPreviewComponent, MatProgressSpinnerModule],
	templateUrl: './pdt-doc-mode.component.html',
	styleUrl: './pdt-doc-mode.component.scss'
})
export class PdtDocModeComponent {

	showPreview: boolean = false;
	isFileUploaded = false;
	fileName: string | null = null;
	fileError: string | null = null;
	selectedFile: File | null = null;
	_quizData: FullQuizData | null = null;
	showValidation = false;
	classroomId!: number;
	quizId!: string;
	subscription = new Subscription();
	@Output() switchMode = new EventEmitter<'manual' | 'ai' | 'course' | 'csv' | 'pdf'>();
	JobId: string = '';
	selectedPDFFile!: File;
	isLoading: boolean = false;
	extractedQuestionsCount: number = 0;
	showUploadResult = false;
	totalPages = 0;
	questionExtracted = 0;
	questionFailedCount = 2;
	failedQuestionDetails: PDFFailedQuestionDetail[] = [];


	constructor(
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private _quizService: QuizManagementService
	) { }


	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.classroomId = +params.get('classroomId')!;
			this.quizId = params.get('quizId')!;
		});
	}

	removeFile(event: Event) {
		event.stopPropagation();
		this.fileName = null;
		this.selectedFile = null;
		this.isFileUploaded = false;
		this.selectedPDFFile = undefined!;
		this.showUploadResult = false;
		this.totalPages = 0;
		this.questionExtracted = 0;
		this.questionFailedCount = 0;
	}


	onPDFSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files || input.files.length === 0) {
			return;
		}
		const file = input.files[0];
		const allowedTypes = [
			'application/pdf',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'application/msword'
		];

		if (!allowedTypes.includes(file.type)) {
			this.toastr.error('Please upload a PDF or DOCX file');
			return;
		}

		if (file.size > 50 * 1024 * 1024) {
			this.toastr.error('File size should be less than 50MB.');
			return;
		}
		this.selectedPDFFile = file;
		this.selectedFile = file;
		this.fileName = file.name;
		this.isFileUploaded = true;
		this.showPreview = true;

		this.postPDFDocUpload();
	}


	postPDFDocUpload() {
		if (!this.selectedPDFFile) {
			this.toastr.error('No file selected');
			return;
		}

		this.isLoading = true;

		const formData = new FormData();
		formData.append('file', this.selectedPDFFile);

		this._quizService.postQuizPDFDocUpload(formData, this.quizId).subscribe({
			next: (response) => {

				if (response.Success) {
					this.toastr.success(response.Message);
					this.JobId = response.Data.job_id;
					this.getPDFDocUploadStatus();
				} else {
					this.isLoading = false;
					this.toastr.error(response.Message);
					this.resetUploadedFile();
				}
			},
			error: () => {
				this.isLoading = false;
				this.toastr.error('Upload failed');
				this.resetUploadedFile();
			}
		});
	}



	getPDFDocUploadStatus() {

		const interval = setInterval(() => {
			this._quizService.getQuizDocumentJobStatusData(this.JobId)
				.subscribe({
					next: (response: any) => {
						if (response.Data.status === 'completed') {
							clearInterval(interval);
							this.isLoading = false;
							this.showUploadResult = true;
							this.totalPages = response.Data.total_pages;
							this.questionExtracted = response.Data.questions_extracted;
							this.questionFailedCount = response.Data.questions_failed;
							this.failedQuestionDetails = response.Data.failed_question_details;
							this.toastr.success('Question generated successfully');
							this.getquizById();
							return;
						}

						if (response.Data.status === 'failed') {
							clearInterval(interval);
							this.isLoading = false;
							this.toastr.error(response.Data.error_message);
							this.resetUploadedFile();
							return;
						}

						if (response.Data.questions_extracted > 0) {
							console.log('Processing...', response.Data.questions_extracted);
						}
					},
					error: () => {
						clearInterval(interval);
						this.isLoading = false;
						this.toastr.error('Error checking document status');
						this.resetUploadedFile();
					}
				});

		}, 3000);
	}


	getquizById() {
		if (this.quizId) {
			this.subscription.add(
				this._quizService.getQuizById(this.quizId).subscribe({
					next: (response) => {
						if (response.Success) {
							this._quizService.saveQuizData.next(response.Data);
							this._quizData = response.Data;
						}
					}
				}));
		}
	}



	private resetUploadedFile(): void {
		this.fileName = null;
		this.selectedFile = null;
		this.selectedPDFFile = undefined!;
		this.isFileUploaded = false;
	}


	downloadPDFTemplate(): void {
		this.subscription.add(
			this._quizService.getPDFTemplateDownload().subscribe({
				next: (response) => {
					const blob = new Blob([response.body!], { type: 'application/pdf' });
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a');

					a.href = url;
					a.download = 'quiz_template.pdf';
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					window.URL.revokeObjectURL(url);
				},
				error: () => {
					this.toastr.error('Failed to download PDF template');
				}
			})
		);
	}


	downloadDOCXTemplate(): void {
		this.subscription.add(
			this._quizService.getDOCXTemplateDownload().subscribe({
				next: (response) => {
					const contentDisposition = response.headers.get('content-disposition');
					let fileName = 'quiz_template.docx';

					if (contentDisposition) {
						const matches = /filename="?([^"]+)"?/.exec(contentDisposition);

						if (matches?.[1]) {
							fileName = matches[1];
						}
					}

					const blob = new Blob([response.body!], {
						type: response.body?.type
					});

					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a')
					a.href = url;
					a.download = fileName;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					window.URL.revokeObjectURL(url);
				},
				error: () => {
					this.toastr.error('Failed to download template');
				}
			})
		);
	}



	failedQuestions = [
		{
			question: 3,
			title: 'Missing SUB CONCEPT NAME header.',
			message: "Add a line 'SUB CONCEPT NAME : <topic>' before the question group."
		},
		{
			question: 5,
			title: 'DIFFICULTY LEVEL value not recognized.',
			message: 'Use one of: Easy, Medium, Hard.'
		},
		{
			question: 7,
			title: 'Could not detect 4 options.',
			message: 'Ensure each option starts with (A), (B), (C), (D).'
		},
		{
			question: 9,
			title: 'Answer marker missing.',
			message: "Add a line like 'Ans.: A' below the options."
		}
	];


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
